import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { LauncherPosition } from '@features/front-chat/types';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import type { SupportDraft, SupportInteraction, SupportView } from '../types';

import { JotAnchoredPopover } from './placement/JotAnchoredPopover';
import { JotLauncher } from './placement/JotLauncher';
import { LAUNCHER_SIZE, type LauncherPlacement } from './placement/launcherPlacement';
import {
  type LauncherPlacementChange,
  useJotLauncherPlacement,
} from './placement/useJotLauncherPlacement';
import { useJotSupportHost } from './placement/useJotSupportHost';
import { SupportComposer } from './SupportComposer';

export interface JotSupportDisplayProps {
  launcherVisible?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  launcherPosition?: LauncherPosition;
  placement?: LauncherPlacement | null;
  onPlacementChange?: (placement: LauncherPlacement, change: LauncherPlacementChange) => void;
  /** Optional companion content anchored to this widget's single launcher. */
  renderLauncherAccessory?: (anchor: {
    launcher: RefObject<HTMLButtonElement | null>;
    reducedMotion: boolean;
  }) => ReactNode;
}

export interface JotSupportWidgetProps extends JotSupportDisplayProps {
  replyEmail: string;
  preparationError?: string;
  view?: SupportView;
  draft: SupportDraft;
  onDraftChange: (draft: SupportDraft) => void;
  onSubmit: () => void;
  onRetry?: () => void;
  onCheckStatus?: () => void;
  onNewRequest?: () => void;
  checkingStatus?: boolean;
  initiallyOpen?: boolean;
  reducedMotion?: boolean;
  onInteraction?: (event: SupportInteraction) => void;
}

const receiptCopy = {
  sending: ['Sending…', ''],
  sent: ['Sent', 'Our team will reply by email.'],
  failure: [
    'Your request couldn’t be delivered',
    'Please email our support team so we can help. Your draft is saved here.',
  ],
  'retryable-failure': [
    'Your request wasn’t sent',
    'Your message and screenshot are saved here. Try sending this same request again.',
  ],
  unknown: [
    'We can’t confirm delivery yet',
    'Your request may have reached support. Check its status before sending another request.',
  ],
} as const;

export function JotSupportWidget({
  replyEmail,
  preparationError,
  view = 'compose',
  draft,
  onDraftChange,
  onSubmit,
  onRetry,
  onCheckStatus,
  onNewRequest,
  checkingStatus = false,
  initiallyOpen = false,
  reducedMotion = false,
  onInteraction,
  launcherVisible = true,
  open: controlledOpen,
  onOpenChange,
  launcherPosition,
  placement: savedPlacement,
  onPlacementChange,
  renderLauncherAccessory,
}: JotSupportWidgetProps) {
  const [internalOpen, setInternalOpen] = useState(initiallyOpen);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (value: boolean) => {
    if (controlledOpen === undefined) setInternalOpen(value);
    onOpenChange?.(value);
  };
  const theme = useTheme();
  const headerContainer = useJotSupportHost()?.headerContainer ?? null;
  const defaultPlacement: LauncherPlacement = {
    docked: false,
    point: { x: launcherPosition?.side === 'right' ? 1 : 0, y: 1 },
  };
  const placement = useJotLauncherPlacement({
    headerContainer,
    dockingAvailable: Boolean(headerContainer),
    initialPlacement: defaultPlacement,
    value: onPlacementChange ? (savedPlacement ?? defaultPlacement) : undefined,
    onChange: onPlacementChange,
    bottomInset: launcherPosition ? Number.parseFloat(theme.spacing(launcherPosition.bottom)) : 0,
  });
  const [reaction, setReaction] = useState(0);
  const launcher = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const setLauncher = useCallback((element: HTMLButtonElement | null) => {
    launcher.current = element;
    setAnchor(element);
  }, []);
  const fallbackAnchor = useMemo(
    () => ({
      getBoundingClientRect: () =>
        new DOMRect(placement.position.x, placement.position.y, LAUNCHER_SIZE, LAUNCHER_SIZE),
    }),
    [placement.position.x, placement.position.y],
  );
  const panel = useRef<HTMLDivElement | null>(null);
  const focusPanelOnMount = useCallback((element: HTMLDivElement | null) => {
    panel.current = element;
    // The composer focuses its message. Receipt-only panels focus their container.
    if (element && !element.contains(document.activeElement))
      element.focus({ preventScroll: true });
  }, []);
  const titleId = useId();
  const dialogId = useId();
  const close = () => {
    setOpen(false);
    onInteraction?.('closed');
    launcher.current?.focus({ preventScroll: true });
  };
  const handleEscape = (event: KeyboardEvent<HTMLElement>) => {
    if (open && event.key === 'Escape' && !event.defaultPrevented) {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };
  const mood = !open ? 'idle' : view === 'sending' ? 'sending' : view === 'sent' ? 'sent' : 'idle';
  const launcherAccessory =
    launcherVisible && !open
      ? renderLauncherAccessory?.({ launcher, reducedMotion: placement.docked || reducedMotion })
      : null;
  return (
    <>
      {launcherVisible && (
        <JotLauncher
          placement={placement}
          headerContainer={headerContainer}
          buttonRef={setLauncher}
          panelId={dialogId}
          open={open}
          mood={mood}
          reaction={reaction}
          reducedMotion={reducedMotion}
          onKeyDown={handleEscape}
          onActivate={() => {
            if (open) close();
            else {
              setReaction(value => value + 1);
              setOpen(true);
              onInteraction?.('opened');
            }
          }}
        />
      )}
      <JotAnchoredPopover
        anchor={anchor}
        docked={placement.docked}
        open={Boolean(launcherAccessory)}
        onClose={() => undefined}
      >
        {launcherAccessory}
      </JotAnchoredPopover>
      {open && (
        <JotAnchoredPopover
          anchor={anchor ?? fallbackAnchor}
          docked={placement.docked}
          open={open}
          onClose={close}
        >
          <Paper
            ref={focusPanelOnMount}
            id={dialogId}
            className="rr-block"
            role="dialog"
            aria-modal={false}
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={handleEscape}
            elevation={8}
            sx={{
              width: 400,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 'inherit',
              zIndex: theme => theme.zIndex.modal - 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              borderRadius: 3,
              outline: 'none',
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ p: 2, pl: 2.5, alignItems: 'center', flexShrink: 0 }}
            >
              <MailOutlineRounded color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography id={titleId} component="h2" variant="h6">
                  Contact support
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  A message to the JotPsych team
                </Typography>
              </Box>
              <IconButton onClick={close} aria-label="Close support">
                <CloseRounded />
              </IconButton>
            </Stack>
            <Divider />
            <Box sx={{ p: 2.5, overflowY: 'auto', minHeight: 0 }}>
              {preparationError && view === 'compose' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {preparationError}
                </Alert>
              )}
              {view === 'compose' ? (
                <SupportComposer
                  draft={draft}
                  onChange={onDraftChange}
                  replyEmail={replyEmail}
                  locked={false}
                  onSubmit={() => {
                    panel.current?.focus({ preventScroll: true });
                    onInteraction?.('submitted');
                    onSubmit();
                  }}
                  onAttachmentSelected={() => onInteraction?.('attachment_selected')}
                />
              ) : (
                <Stack spacing={2.5} sx={{ py: 1, textAlign: 'center', alignItems: 'center' }}>
                  <Box
                    sx={{
                      alignSelf: 'flex-end',
                      maxWidth: '90%',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: '16px 16px 4px 16px',
                      p: 1.5,
                      textAlign: 'left',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                    >
                      {draft.message}
                    </Typography>
                    {draft.screenshot && (
                      <Typography
                        variant="caption"
                        sx={{ mt: 1, display: 'block', overflowWrap: 'anywhere' }}
                      >
                        Screenshot: {draft.screenshot.name}
                      </Typography>
                    )}
                  </Box>
                  <Box role="status" aria-live="polite" aria-atomic="true">
                    <Typography
                      variant={view === 'sending' ? 'body2' : 'h6'}
                      component="h3"
                      sx={{ mb: 1 }}
                    >
                      {receiptCopy[view][0]}
                    </Typography>
                    {receiptCopy[view][1] && (
                      <Typography variant="body2" color="text.secondary">
                        {receiptCopy[view][1]}
                      </Typography>
                    )}
                  </Box>
                  {view === 'sent' && (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <CheckCircleOutlineRounded color="success" fontSize="small" />
                      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                        {replyEmail}
                      </Typography>
                    </Stack>
                  )}
                  {view === 'retryable-failure' && onRetry && (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        panel.current?.focus({ preventScroll: true });
                        onInteraction?.('retry_clicked');
                        onRetry();
                      }}
                    >
                      Try again
                    </Button>
                  )}
                  {view === 'unknown' && onCheckStatus && (
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={checkingStatus}
                      onClick={() => {
                        onInteraction?.('status_requested');
                        onCheckStatus();
                      }}
                    >
                      {checkingStatus ? 'Checking status…' : 'Check delivery status'}
                    </Button>
                  )}
                  {(view === 'unknown' || view === 'failure') && (
                    <Button
                      href="mailto:support@jotpsych.com?subject=Support%20request"
                      startIcon={<MailOutlineRounded />}
                      size="small"
                    >
                      Email support
                    </Button>
                  )}
                  {view === 'sent' && (
                    <Button variant="contained" fullWidth onClick={close}>
                      Done
                    </Button>
                  )}
                  {view === 'sent' && onNewRequest && (
                    <Button
                      size="small"
                      onClick={() => {
                        panel.current?.focus({ preventScroll: true });
                        onNewRequest();
                      }}
                    >
                      Start another request
                    </Button>
                  )}
                </Stack>
              )}
            </Box>
            {(placement.docked || headerContainer) && (
              <>
                <Divider />
                <Stack
                  direction="row"
                  sx={{
                    px: 2.5,
                    py: 1,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Your draft stays here.
                  </Typography>
                  <Button
                    size="small"
                    sx={{ textTransform: 'none' }}
                    onClick={event =>
                      placement.setDocked(
                        !placement.docked,
                        event.detail === 0 ? 'keyboard' : 'pointer',
                      )
                    }
                  >
                    {placement.docked ? 'Undock Jot' : 'Dock in header'}
                  </Button>
                </Stack>
              </>
            )}
          </Paper>
        </JotAnchoredPopover>
      )}
    </>
  );
}
